"use client";

import React, { useEffect, useRef, useState } from "react";
import { Maximize2, Droplets, ShieldCheck, Zap, Info, Cpu, Globe } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const pillarsData = [{
  id: "scalability",
  title: "Geographic Diversification",
  subtitle: "Infinite Expansion",
  description: "Balanced exposure across the US, India, and LATAM ensures resilience against regional shocks, regulatory shifts, and currency volatility while capturing growth from distinct digital expansion cycles.",
  hoverContext: "Compute demands for frontier models are doubling every 6 months. We invest exclusively in modular, hyper-scalable architectures that anticipate this curve, ensuring our portfolio assets never become the bottleneck.",
  icon: Globe
}, {
  id: "efficiency",
  title: "Energy & Efficiency Advantage",
  subtitle: "Liquid Cooling Focus",
  description: "Renewable integration and advanced liquid cooling reduce operating intensity, protect margin durability, and enhance attractiveness to institutional buyers at exit.",
  hoverContext: "Traditional air cooling hits a physical limit at 30kW per rack. Our thesis centers on direct-to-chip and immersion cooling technologies, enabling 100kW+ racks while drastically slashing Power Usage Effectiveness (PUE).",
  icon: Droplets
}, {
  id: "sovereignty",
  title: "Flexible Capital Pathways",
  subtitle: "On-shore Compute",
  description: "Multiple exit routes — power-ready exits, pad-ready monetization, stabilized yield conversion, and full platform sale — reduce hold-period risk and maximize return certainty for LPs.",
  hoverContext: "Compute is the new sovereign asset. We prioritize investments in localized, jurisdictionally secure facilities that guarantee data privacy, regulatory compliance, and protection against global supply shocks.",
  icon: ShieldCheck
}, {
  id: "resilience",
  title: "Modular Scale & Aggregation\n\n",
  subtitle: "Redundant Power",
  description: "20–40MW phased deployment with capital-efficient scaling and institutional-grade campus aggregation. Platform scale supports premium exit multiples versus single-asset transactions.",
  hoverContext: "AI infrastructure cannot afford downtime. We back facilities integrating on-site generation, utility-scale battery storage, and multi-grid connections to guarantee continuous operation under extreme load and grid instability.",
  icon: Zap
}];
export default function InvestmentPillars() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section id="investment-pillars" className="bg-background text-foreground py-24 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
        <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
          <defs>
            <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" className="text-accent" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-pattern)" />
        </svg>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl mb-16 md:mb-24">
          {}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-accent rounded-full bg-transparent text-accent text-xs font-semibold uppercase tracking-widest mb-8">
            <Cpu className="size-3" />
            <span>Structured Value Creation</span>
          </div>
          {}
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter mb-6 text-foreground">
            How we&nbsp;<br />
            <span className="text-accent">Protect and Grow Returns</span>
          </h2>
          <p className="text-lg md:text-xl text-foreground/70 max-w-2xl leading-relaxed">N2N Capital enhances return durability through integrated execution and disciplined capital structuring across five distinct value levers.</p>
        </div>

        <div ref={ref} className={`grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {pillarsData.map((pillar, index) => {
          const Icon = pillar.icon;
          return <div key={pillar.id} data-index={index} className="group h-full">
                {}
                <Card className="h-full flex flex-col bg-card border-accent/10 hover:border-accent/40 transition-all duration-500 rounded-xl shadow-sm hover:shadow-md">
                  <CardHeader className="pb-4">
                    <div className="mb-6 p-4 bg-background w-fit rounded-lg border border-accent/10 group-hover:border-accent/30 transition-colors">
                      <Icon className="size-8 text-accent" strokeWidth={1.5} />
                    </div>
                    <CardTitle className="text-2xl font-bold tracking-tight mb-2 text-foreground">
                      {pillar.title}
                    </CardTitle>
                    <CardDescription className="text-accent font-semibold text-sm uppercase tracking-wider">
                      {pillar.subtitle}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-grow flex flex-col justify-between gap-8">
                    <p className="text-foreground/70 leading-relaxed">
                      {pillar.description}
                    </p>

                    
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}